import React, { useEffect, useState, useMemo } from 'react';
import useIsMobile from '../hooks/useIsMobile';

const FEATURE_LABELS = {
  REVENUE_MANAGER: 'Revenue Manager',
  GUEST_RECOGNITION: 'Guest Recognition',
  NO_SHOW_PREDICTOR: 'No-Show Predictor',
  OCCUPANCY_FORECAST: 'Occupancy Forecast',
  UPSELL_ENGINE: 'Upsell Engine',
  STAFF_ASSISTANT: 'Staff Assistant',
  FOLIO_ANOMALY: 'Folio Anomaly',
  CORPORATE_CREDIT_RISK: 'Corporate Risk'
};

function AiUsageDashboard({ token, selectedProperty }) {
  const { isMobile } = useIsMobile();
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [totals, setTotals] = useState({});
  const [byFeature, setByFeature] = useState([]);
  const [daily, setDaily] = useState([]);
  const [logRows, setLogRows] = useState([]);
  const [logLoading, setLogLoading] = useState(false);
  const [logOffset, setLogOffset] = useState(0);
  const [tab, setTab] = useState('summary');

  // Global view — chain_id and property_id are optional
  const chainId = String(selectedProperty?.chain_id || '').trim();
  const propertyId = String(selectedProperty?.id || '').trim();
  const headers = () => ({
    Authorization: `Bearer ${token}`,
    ...(chainId ? { 'x-chain-id': chainId } : {}),
    ...(propertyId ? { 'x-property-id': propertyId } : {})
  });

  const loadSummary = async () => {
    setLoading(true);
    setError('');
    try {
      const url = `/api/v1/ai/usage-summary?days=${days}${propertyId ? `&property_id=${propertyId}` : ''}`;
      const res = await fetch(url, { headers: headers() });
      const ct = res.headers.get('content-type') || '';
      if (!ct.includes('application/json')) throw new Error('Server unavailable — please try again');
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Failed to load');
      setTotals(data.totals || {});
      setByFeature(data.by_property_feature || []);
      setDaily(data.daily || []);
    } catch (e) {
      setError(e.message || 'Network error');
    } finally {
      setLoading(false);
    }
  };

  const loadLog = async (offset = 0) => {
    setLogLoading(true);
    try {
      const url = `/api/v1/ai/usage-log?limit=50&offset=${offset}${propertyId ? `&property_id=${propertyId}` : ''}`;
      const res = await fetch(url, { headers: headers() });
      const ct = res.headers.get('content-type') || '';
      if (!ct.includes('application/json')) throw new Error('Server unavailable');
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Failed to load log');
      setLogRows(data.rows || []);
      setLogOffset(offset);
    } catch (e) {
      setError(e.message);
    } finally {
      setLogLoading(false);
    }
  };

  useEffect(() => { loadSummary(); }, [token, chainId, propertyId, days]);

  const fmt = (n) => n != null ? Number(n).toLocaleString() : '0';
  const fmtCost = (n) => n != null ? `$${Number(n).toFixed(4)}` : '$0.0000';

  // Aggregate by feature for the summary
  const featureSummary = useMemo(() => {
    const map = {};
    for (const row of byFeature) {
      const key = row.feature_key;
      if (!map[key]) map[key] = { feature_key: key, api_calls: 0, total_tokens: 0, total_cost: 0, total_input_tokens: 0, total_output_tokens: 0 };
      map[key].api_calls += Number(row.api_calls) || 0;
      map[key].total_tokens += Number(row.total_tokens) || 0;
      map[key].total_cost += Number(row.total_cost) || 0;
      map[key].total_input_tokens += Number(row.total_input_tokens) || 0;
      map[key].total_output_tokens += Number(row.total_output_tokens) || 0;
    }
    return Object.values(map).sort((a, b) => b.total_cost - a.total_cost);
  }, [byFeature]);

  // Aggregate by property for global view
  const propertySummary = useMemo(() => {
    const map = {};
    for (const row of byFeature) {
      const key = row.property_id || 'unknown';
      if (!map[key]) map[key] = { property_id: key, property_name: row.property_name || key, api_calls: 0, total_tokens: 0, total_cost: 0 };
      map[key].api_calls += Number(row.api_calls) || 0;
      map[key].total_tokens += Number(row.total_tokens) || 0;
      map[key].total_cost += Number(row.total_cost) || 0;
    }
    return Object.values(map).sort((a, b) => b.total_cost - a.total_cost);
  }, [byFeature]);

  const featureColors = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316'];

  return (
    <div className="page-shell">
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.78rem', marginBottom: '0.6rem' }}>
        <span style={{ color: '#6b7280', fontWeight: 500 }}>Settings</span>
        <span style={{ color: '#cbd5e0' }}>&rsaquo;</span>
        <span style={{ color: 'var(--theme-primary-dark)', fontWeight: 600 }}>AI Usage Dashboard</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
        <div>
          <h2 style={{ color: 'var(--theme-primary-dark)', margin: 0 }}>AI Usage Dashboard</h2>
          <div style={{ fontSize: '0.78rem', color: '#6b7280', marginTop: '0.2rem' }}>
            {propertyId ? `Showing: ${selectedProperty?.name || propertyId}` : 'Showing: All Properties (Global)'}
          </div>
        </div>
        <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
          {[7, 30, 90].map(d => (
            <button key={d} onClick={() => setDays(d)}
              style={{
                padding: '0.35rem 0.8rem', borderRadius: 5, border: '1px solid var(--theme-border)',
                background: days === d ? 'var(--theme-primary)' : '#fff',
                color: days === d ? '#fff' : 'var(--theme-text)',
                fontWeight: days === d ? 700 : 400, cursor: 'pointer', fontSize: '0.82rem'
              }}>
              {d}d
            </button>
          ))}
          <button onClick={loadSummary} disabled={loading}
            className="btn-muted" style={{ padding: '0.35rem 0.8rem', fontSize: '0.82rem', marginLeft: '0.3rem' }}>
            {loading ? 'Loading...' : 'Refresh'}
          </button>
        </div>
      </div>

      {error && <div className="error-banner">{error}</div>}

      {/* Summary Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.8rem', marginBottom: '1.2rem' }}>
        {[
          { label: 'Total API Calls', value: fmt(totals.total_calls), color: '#6366f1' },
          { label: 'Input Tokens', value: fmt(totals.total_input_tokens), color: '#10b981' },
          { label: 'Output Tokens', value: fmt(totals.total_output_tokens), color: '#f59e0b' },
          { label: 'Total Tokens', value: fmt(totals.total_tokens), color: '#8b5cf6' },
          { label: 'Estimated Cost', value: fmtCost(totals.total_cost), color: '#ef4444' }
        ].map((c, i) => (
          <div key={i} className="card" style={{ padding: '1rem', textAlign: 'center' }}>
            <div style={{ fontSize: '0.72rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.4rem' }}>
              {c.label}
            </div>
            <div style={{ fontSize: '1.4rem', fontWeight: 700, color: c.color }}>
              {loading ? '...' : c.value}
            </div>
          </div>
        ))}
      </div>

      {/* Tab bar */}
      <div style={{ display: 'flex', gap: 0, borderBottom: '2px solid var(--theme-border)', marginBottom: '1rem' }}>
        {[
          { key: 'summary', label: 'By Feature' },
          ...(propertySummary.length > 1 ? [{ key: 'property', label: 'By Property' }] : []),
          { key: 'daily', label: 'Daily Trend' },
          { key: 'log', label: 'Detailed Log' }
        ].map(t => (
          <button key={t.key} onClick={() => { setTab(t.key); if (t.key === 'log' && logRows.length === 0) loadLog(0); }}
            style={{
              padding: '0.5rem 1.2rem', border: 'none', borderBottom: tab === t.key ? '2px solid var(--theme-primary)' : '2px solid transparent',
              background: 'none', fontWeight: tab === t.key ? 700 : 400,
              color: tab === t.key ? 'var(--theme-primary)' : '#6b7280',
              cursor: 'pointer', fontSize: '0.85rem', marginBottom: '-2px'
            }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* By Feature Tab */}
      {tab === 'summary' && (
        <div className="card">
          <div className="card-body" style={{ overflowX: 'auto' }}>
            {loading ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>Loading...</div>
            ) : featureSummary.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>No AI usage data for this period.</div>
            ) : (
              <>
                {/* Visual bars */}
                <div style={{ marginBottom: '1.5rem' }}>
                  {featureSummary.map((f, i) => {
                    const maxCost = featureSummary[0]?.total_cost || 1;
                    const pct = Math.max((f.total_cost / maxCost) * 100, 2);
                    return (
                      <div key={f.feature_key} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.5rem' }}>
                        <div style={{ width: isMobile ? 100 : 160, fontSize: '0.82rem', fontWeight: 500, color: 'var(--theme-text)', flexShrink: 0 }}>
                          {FEATURE_LABELS[f.feature_key] || f.feature_key}
                        </div>
                        <div style={{ flex: 1, background: '#f3f4f6', borderRadius: 4, height: 22, position: 'relative' }}>
                          <div style={{
                            width: `${pct}%`, height: '100%', borderRadius: 4,
                            background: featureColors[i % featureColors.length],
                            transition: 'width 0.4s ease'
                          }} />
                        </div>
                        <div style={{ width: 80, textAlign: 'right', fontSize: '0.82rem', fontWeight: 600, color: 'var(--theme-text)', flexShrink: 0 }}>
                          {fmtCost(f.total_cost)}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Detail table */}
                <table style={{ width: '100%', fontSize: '0.82rem', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid var(--theme-border)', textAlign: 'left' }}>
                      <th style={{ padding: '0.5rem 0.4rem', fontWeight: 600, color: '#6b7280' }}>Feature</th>
                      <th style={{ padding: '0.5rem 0.4rem', fontWeight: 600, color: '#6b7280', textAlign: 'right' }}>Calls</th>
                      <th style={{ padding: '0.5rem 0.4rem', fontWeight: 600, color: '#6b7280', textAlign: 'right' }}>Input Tokens</th>
                      <th style={{ padding: '0.5rem 0.4rem', fontWeight: 600, color: '#6b7280', textAlign: 'right' }}>Output Tokens</th>
                      <th style={{ padding: '0.5rem 0.4rem', fontWeight: 600, color: '#6b7280', textAlign: 'right' }}>Total Tokens</th>
                      <th style={{ padding: '0.5rem 0.4rem', fontWeight: 600, color: '#6b7280', textAlign: 'right' }}>Cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {featureSummary.map((f, i) => (
                      <tr key={f.feature_key} style={{ borderBottom: '1px solid var(--theme-border-light)' }}>
                        <td style={{ padding: '0.5rem 0.4rem', fontWeight: 500 }}>
                          <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: featureColors[i % featureColors.length], marginRight: 6, verticalAlign: 'middle' }} />
                          {FEATURE_LABELS[f.feature_key] || f.feature_key}
                        </td>
                        <td style={{ padding: '0.5rem 0.4rem', textAlign: 'right' }}>{fmt(f.api_calls)}</td>
                        <td style={{ padding: '0.5rem 0.4rem', textAlign: 'right' }}>{fmt(f.total_input_tokens)}</td>
                        <td style={{ padding: '0.5rem 0.4rem', textAlign: 'right' }}>{fmt(f.total_output_tokens)}</td>
                        <td style={{ padding: '0.5rem 0.4rem', textAlign: 'right', fontWeight: 600 }}>{fmt(f.total_tokens)}</td>
                        <td style={{ padding: '0.5rem 0.4rem', textAlign: 'right', fontWeight: 600, color: '#ef4444' }}>{fmtCost(f.total_cost)}</td>
                      </tr>
                    ))}
                    <tr style={{ borderTop: '2px solid var(--theme-border)', fontWeight: 700 }}>
                      <td style={{ padding: '0.6rem 0.4rem' }}>Total</td>
                      <td style={{ padding: '0.6rem 0.4rem', textAlign: 'right' }}>{fmt(totals.total_calls)}</td>
                      <td style={{ padding: '0.6rem 0.4rem', textAlign: 'right' }}>{fmt(totals.total_input_tokens)}</td>
                      <td style={{ padding: '0.6rem 0.4rem', textAlign: 'right' }}>{fmt(totals.total_output_tokens)}</td>
                      <td style={{ padding: '0.6rem 0.4rem', textAlign: 'right' }}>{fmt(totals.total_tokens)}</td>
                      <td style={{ padding: '0.6rem 0.4rem', textAlign: 'right', color: '#ef4444' }}>{fmtCost(totals.total_cost)}</td>
                    </tr>
                  </tbody>
                </table>
              </>
            )}
          </div>
        </div>
      )}

      {/* By Property Tab — only shows when multiple properties have data */}
      {tab === 'property' && (
        <div className="card">
          <div className="card-body" style={{ overflowX: 'auto' }}>
            {loading ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>Loading...</div>
            ) : (
              <table style={{ width: '100%', fontSize: '0.82rem', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid var(--theme-border)', textAlign: 'left' }}>
                    <th style={{ padding: '0.5rem 0.4rem', fontWeight: 600, color: '#6b7280' }}>Property</th>
                    <th style={{ padding: '0.5rem 0.4rem', fontWeight: 600, color: '#6b7280', textAlign: 'right' }}>API Calls</th>
                    <th style={{ padding: '0.5rem 0.4rem', fontWeight: 600, color: '#6b7280', textAlign: 'right' }}>Total Tokens</th>
                    <th style={{ padding: '0.5rem 0.4rem', fontWeight: 600, color: '#6b7280', textAlign: 'right' }}>Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {propertySummary.map((p, i) => (
                    <tr key={p.property_id} style={{ borderBottom: '1px solid var(--theme-border-light)' }}>
                      <td style={{ padding: '0.5rem 0.4rem', fontWeight: 500 }}>
                        <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: featureColors[i % featureColors.length], marginRight: 6, verticalAlign: 'middle' }} />
                        {p.property_name}
                      </td>
                      <td style={{ padding: '0.5rem 0.4rem', textAlign: 'right' }}>{fmt(p.api_calls)}</td>
                      <td style={{ padding: '0.5rem 0.4rem', textAlign: 'right', fontWeight: 600 }}>{fmt(p.total_tokens)}</td>
                      <td style={{ padding: '0.5rem 0.4rem', textAlign: 'right', fontWeight: 600, color: '#ef4444' }}>{fmtCost(p.total_cost)}</td>
                    </tr>
                  ))}
                  <tr style={{ borderTop: '2px solid var(--theme-border)', fontWeight: 700 }}>
                    <td style={{ padding: '0.6rem 0.4rem' }}>Total</td>
                    <td style={{ padding: '0.6rem 0.4rem', textAlign: 'right' }}>{fmt(totals.total_calls)}</td>
                    <td style={{ padding: '0.6rem 0.4rem', textAlign: 'right' }}>{fmt(totals.total_tokens)}</td>
                    <td style={{ padding: '0.6rem 0.4rem', textAlign: 'right', color: '#ef4444' }}>{fmtCost(totals.total_cost)}</td>
                  </tr>
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* Daily Trend Tab */}
      {tab === 'daily' && (
        <div className="card">
          <div className="card-body" style={{ overflowX: 'auto' }}>
            {loading ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>Loading...</div>
            ) : daily.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>No daily data for this period.</div>
            ) : (
              <>
                {/* Simple bar chart */}
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 160, marginBottom: '1rem', padding: '0 0.2rem' }}>
                  {(() => {
                    const dateMap = {};
                    for (const d of daily) {
                      if (!dateMap[d.date]) dateMap[d.date] = { date: d.date, calls: 0, tokens: 0, cost: 0 };
                      dateMap[d.date].calls += Number(d.api_calls) || 0;
                      dateMap[d.date].tokens += Number(d.total_tokens) || 0;
                      dateMap[d.date].cost += Number(d.total_cost) || 0;
                    }
                    const arr = Object.values(dateMap).sort((a, b) => a.date.localeCompare(b.date));
                    const maxTokens = Math.max(...arr.map(d => d.tokens), 1);
                    return arr.map((d, i) => (
                      <div key={d.date} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 0 }}
                        title={`${d.date}\nCalls: ${d.calls}\nTokens: ${fmt(d.tokens)}\nCost: ${fmtCost(d.cost)}`}>
                        <div style={{
                          width: '100%', maxWidth: 28,
                          height: Math.max((d.tokens / maxTokens) * 140, 3),
                          background: 'var(--theme-primary)',
                          borderRadius: '3px 3px 0 0',
                          opacity: 0.8
                        }} />
                        {arr.length <= 14 && (
                          <div style={{ fontSize: '0.62rem', color: '#9ca3af', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 40 }}>
                            {d.date.slice(5)}
                          </div>
                        )}
                      </div>
                    ));
                  })()}
                </div>

                {/* Daily table */}
                <table style={{ width: '100%', fontSize: '0.82rem', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid var(--theme-border)', textAlign: 'left' }}>
                      <th style={{ padding: '0.5rem 0.4rem', fontWeight: 600, color: '#6b7280' }}>Date</th>
                      <th style={{ padding: '0.5rem 0.4rem', fontWeight: 600, color: '#6b7280' }}>Feature</th>
                      <th style={{ padding: '0.5rem 0.4rem', fontWeight: 600, color: '#6b7280', textAlign: 'right' }}>Calls</th>
                      <th style={{ padding: '0.5rem 0.4rem', fontWeight: 600, color: '#6b7280', textAlign: 'right' }}>Tokens</th>
                      <th style={{ padding: '0.5rem 0.4rem', fontWeight: 600, color: '#6b7280', textAlign: 'right' }}>Cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {daily.map((d, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid var(--theme-border-light)' }}>
                        <td style={{ padding: '0.4rem', fontWeight: 500 }}>{d.date}</td>
                        <td style={{ padding: '0.4rem' }}>{FEATURE_LABELS[d.feature_key] || d.feature_key}</td>
                        <td style={{ padding: '0.4rem', textAlign: 'right' }}>{fmt(d.api_calls)}</td>
                        <td style={{ padding: '0.4rem', textAlign: 'right' }}>{fmt(d.total_tokens)}</td>
                        <td style={{ padding: '0.4rem', textAlign: 'right', color: '#ef4444', fontWeight: 600 }}>{fmtCost(d.total_cost)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
          </div>
        </div>
      )}

      {/* Detailed Log Tab */}
      {tab === 'log' && (
        <div className="card">
          <div className="card-body" style={{ overflowX: 'auto' }}>
            {logLoading ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>Loading...</div>
            ) : logRows.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>No log entries found.</div>
            ) : (
              <>
                <table style={{ width: '100%', fontSize: '0.78rem', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid var(--theme-border)', textAlign: 'left' }}>
                      <th style={{ padding: '0.4rem', fontWeight: 600, color: '#6b7280' }}>Time</th>
                      <th style={{ padding: '0.4rem', fontWeight: 600, color: '#6b7280' }}>Property</th>
                      <th style={{ padding: '0.4rem', fontWeight: 600, color: '#6b7280' }}>Feature</th>
                      <th style={{ padding: '0.4rem', fontWeight: 600, color: '#6b7280' }}>Provider</th>
                      <th style={{ padding: '0.4rem', fontWeight: 600, color: '#6b7280' }}>Model</th>
                      <th style={{ padding: '0.4rem', fontWeight: 600, color: '#6b7280', textAlign: 'right' }}>In Tokens</th>
                      <th style={{ padding: '0.4rem', fontWeight: 600, color: '#6b7280', textAlign: 'right' }}>Out Tokens</th>
                      <th style={{ padding: '0.4rem', fontWeight: 600, color: '#6b7280', textAlign: 'right' }}>Cost</th>
                      <th style={{ padding: '0.4rem', fontWeight: 600, color: '#6b7280', textAlign: 'right' }}>Time(ms)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logRows.map((r, i) => (
                      <tr key={r.id || i} style={{ borderBottom: '1px solid var(--theme-border-light)' }}>
                        <td style={{ padding: '0.4rem', whiteSpace: 'nowrap' }}>
                          {new Date(r.created_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                        </td>
                        <td style={{ padding: '0.4rem' }}>{r.property_name || r.property_id}</td>
                        <td style={{ padding: '0.4rem', fontWeight: 500 }}>{FEATURE_LABELS[r.feature_key] || r.feature_key}</td>
                        <td style={{ padding: '0.4rem' }}>{r.ai_provider}</td>
                        <td style={{ padding: '0.4rem', fontSize: '0.72rem', color: '#6b7280' }}>{r.ai_model}</td>
                        <td style={{ padding: '0.4rem', textAlign: 'right' }}>{fmt(r.input_tokens)}</td>
                        <td style={{ padding: '0.4rem', textAlign: 'right' }}>{fmt(r.output_tokens)}</td>
                        <td style={{ padding: '0.4rem', textAlign: 'right', fontWeight: 600, color: '#ef4444' }}>{fmtCost(r.estimated_cost)}</td>
                        <td style={{ padding: '0.4rem', textAlign: 'right', color: '#6b7280' }}>{fmt(r.response_time_ms)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '0.8rem', paddingTop: '0.6rem', borderTop: '1px solid var(--theme-border-light)' }}>
                  <button disabled={logOffset === 0 || logLoading} onClick={() => loadLog(Math.max(logOffset - 50, 0))}
                    className="btn-muted" style={{ padding: '0.35rem 0.8rem', fontSize: '0.82rem' }}>
                    Previous
                  </button>
                  <span style={{ fontSize: '0.78rem', color: '#6b7280' }}>
                    Showing {logOffset + 1} - {logOffset + logRows.length}
                  </span>
                  <button disabled={logRows.length < 50 || logLoading} onClick={() => loadLog(logOffset + 50)}
                    className="btn-muted" style={{ padding: '0.35rem 0.8rem', fontSize: '0.82rem' }}>
                    Next
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default AiUsageDashboard;
